import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { BILLING_ENFORCEMENT_KEYS } from '../constants/queryKeys';
import { isUnauthorizedError } from '../services/authErrorHelper';
import {
  billingEnforcementService,
  type BillingEnforcementPolicy,
} from '../services/billingEnforcementService';
import { isTransientBillingPolicyFetchError } from '../utils/billingPolicyFetchErrors';

/** How long policy data is treated as fresh without an explicit refetch. */
const STALE_TIME_MS = 2 * 60 * 1000;

/**
 * When billing is not `active`, poll occasionally so banners / suspension UX pick up Stripe
 * and backend transitions without a full reload. `active` orgs do not poll on a timer (signal
 * changes rarely); they still pick up updates via explicit `refetchOnWindowFocus` /
 * `refetchOnReconnect` on this query (global QueryClient defaults disable those, so this hook
 * turns them back on).
 */
const NON_ACTIVE_POLL_INTERVAL_MS = 90 * 1000;

/** While GET /billing-enforcement/me fails with a transient error, poll until recovery. */
const TRANSIENT_ERROR_REFETCH_MS = 15 * 1000;

/**
 * Fetches GET `/billing-enforcement/me` for the active org.
 *
 * The query runs only when **`enabled` is true and `organizationId` is a non-empty string**
 * (`gateEnabled`). Passing `enabled: true` alone does **not** start the fetch if org id is missing
 * or blank — both conditions are required so the React Query key stays scoped to one organization.
 *
 * @param enabled - When false, the query is disabled regardless of org id.
 * @param organizationId - Active organization id for cache scoping; must be non-empty for the query to run.
 */
export function useBillingEnforcementPolicy(
  enabled: boolean = true,
  organizationId: string | null | undefined = undefined,
) {
  const orgKey = organizationId ?? null;
  const gateEnabled = enabled && orgKey != null && orgKey !== '';
  return useQuery<BillingEnforcementPolicy | null>({
    queryKey: BILLING_ENFORCEMENT_KEYS.me(orgKey),
    queryFn: billingEnforcementService.getMyPolicy,
    enabled: gateEnabled,
    staleTime: STALE_TIME_MS,
    /** Recover from API blips without a full reload (global defaults disable reconnect/window refetch). */
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      if (isUnauthorizedError(error)) return false;
      if (axios.isAxiosError(error) && error.response?.status === 403) return false;
      if (isTransientBillingPolicyFetchError(error)) return failureCount < 5;
      return failureCount < 2;
    },
    refetchInterval: (query) => {
      if (!gateEnabled) return false;
      const { status, error, data: policy } = query.state;
      if (status === 'error' && error != null && isTransientBillingPolicyFetchError(error)) {
        return TRANSIENT_ERROR_REFETCH_MS;
      }
      if (policy == null || policy.state === 'active') return false;
      return NON_ACTIVE_POLL_INTERVAL_MS;
    },
    refetchIntervalInBackground: true,
  });
}
